import React from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'

import OpenIcon from '@common/assets/svg/OpenIcon'
import Button from '@common/components/Button'
import Spinner from '@common/components/Spinner'
import Text from '@common/components/Text'
import StatusBody from '@common/components/TrackProgress/StatusBody'
import { StatusPanelRow } from '@common/components/TrackProgress/StatusPanel'
import { RailState, RailStep } from '@common/components/TrackProgress/StatusRail'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import flexbox from '@common/styles/utils/flexbox'
import { openInTab } from '@web/extension-services/background/webapi/tab'
import type { DcaFlowStep } from '@web/modules/swap-and-bridge/hooks/useDca'
import { getUiType } from '@web/utils/uiType'

import { STATUS_PULSE_ROOM } from './DcaModalShell'

const { isTab } = getUiType()

export type DcaFlowPhase = 'preparing' | 'running' | 'success' | 'error'

type FlowButton = { text: string; onPress: () => void; testID: string }

type Props = {
  phase: DcaFlowPhase
  steps: DcaFlowStep[]
  /** Rail label of a step: `label` once done (or pending), `activeLabel` while it runs. */
  getStepLabels: (step: DcaFlowStep) => { label: string; activeLabel: string }
  title: string
  subtitle: string
  rows?: (StatusPanelRow | null | false | undefined)[]
  explorerUrl?: string | null
  primary?: FlowButton
  secondary?: FlowButton
  testID?: string
}

const RAIL_STATE: Record<DcaFlowStep['status'], RailState> = {
  next: 'pending',
  signing: 'active',
  confirming: 'active',
  done: 'done',
  failed: 'failed'
}

/**
 * The DCA create / cancel progress, built from the same pieces as the Swap &
 * Bridge status popup (TrackProgress): one rail node per transaction, the
 * title, a summary panel, the explorer link and the action buttons.
 */
const DcaFlowStatus = ({
  phase,
  steps,
  getStepLabels,
  title,
  subtitle,
  rows,
  explorerUrl,
  primary,
  secondary,
  testID
}: Props) => {
  const { t } = useTranslation()
  const { theme, themeType } = useTheme()

  const railSteps: RailStep[] = steps.map((step) => ({
    ...getStepLabels(step),
    state: RAIL_STATE[step.status]
  }))
  // The last transaction sent so far: the one worth opening in the explorer.
  const lastTxHash = [...steps].reverse().find((s) => !!s.txHash)?.txHash
  const explorerLink =
    lastTxHash && explorerUrl ? `${explorerUrl.replace(/\/$/, '')}/tx/${lastTxHash}` : undefined

  // Same buttons as TrackProgressWrapper: accent-chip secondary, filled primary.
  const secondaryLabelColor = (
    themeType === THEME_TYPES.DARK ? theme.white : theme.accentChipDecorative
  ) as string

  return (
    <View style={{ width: '100%', paddingTop: STATUS_PULSE_ROOM }} testID={testID}>
      <StatusBody
        steps={railSteps}
        title={title}
        titleTestID="dca-flow-title"
        subtitle={subtitle}
        rows={rows}
        explorerLink={explorerLink}
        openExplorerText={t('View transaction')}
        openInTab={openInTab}
        linkAccessory={
          <OpenIcon color={theme.primary} width={16} height={16} style={spacings.mlTy} />
        }
        // The 600px popup: rail + 5-row panel + buttons only just overflow the
        // sheet, which pushed the buttons below the fold.
        compact={!isTab}
        topSlot={
          railSteps.length ? null : <Spinner style={{ width: 24, height: 24, ...spacings.mbSm }} />
        }
      />

      {(!!primary || !!secondary) && (
        <View style={[flexbox.directionRow, flexbox.alignCenter, { gap: 12, marginTop: isTab ? 28 : 16 }]}>
          {!!secondary && (
            <Pressable
              onPress={secondary.onPress}
              testID={secondary.testID}
              style={({ hovered }: any) => [
                flexbox.center,
                {
                  flex: 1,
                  minWidth: 0,
                  height: 48,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: theme.accentChipDecorative,
                  backgroundColor: theme.accentChipBackground,
                  opacity: hovered ? 0.85 : 1
                }
              ]}
            >
              <Text
                fontSize={16}
                weight="medium"
                color={secondaryLabelColor}
                numberOfLines={2}
                style={{ textAlign: 'center', paddingHorizontal: 8 }}
              >
                {secondary.text}
              </Text>
            </Pressable>
          )}
          {!!primary && (
            <Button
              text={primary.text}
              onPress={primary.onPress}
              type={phase === 'error' ? 'secondary' : 'primary'}
              hasBottomSpacing={false}
              style={{ flex: 1, minWidth: 0, height: 48, borderRadius: 8, marginLeft: 0 }}
              testID={primary.testID}
            />
          )}
        </View>
      )}
    </View>
  )
}

export default React.memo(DcaFlowStatus)
